import React, { useState, useMemo, useCallback } from 'react';
import { Flex, Button, Pagination } from 'antd';
import { FREE_ASSETS } from '@src/components/side-bar-menu/web2-asset-store/items/free-assets';
import ModelViewerModal from '@src/components/side-bar-menu/web2-asset-store/ModelViewer';
import styles from '../add-to-scene.module.css';

const PAGE_LIMIT = 18;

const CATEGORIES = {
  WORLDS: 'worlds',
  PREFABS: 'prefabs',
};

function paginate(array: any[], currentPage: number): any[] {
  return array.slice(currentPage * PAGE_LIMIT, (currentPage + 1) * PAGE_LIMIT);
}

const Card = ({
  item,
  onLoadWorld,
  activeCategory,
  selectedWorld,
  onOpenPreview,
}) => {
  const isWorld = activeCategory === CATEGORIES.WORLDS;

  const url = isWorld ? item.baseModelURL || item.url : item.assetURL;
  const thumbnail = isWorld
    ? item.thumbnail
    : item.previewImage || item.thumbnail;

  const isSelected = selectedWorld === url;

  const handleImageClick = (e) => {
    e.stopPropagation();
    if (url && !isWorld) {
      onOpenPreview(url);
    }
  };

  return (
    <div className={styles.worldCard}>
      <div
        className={`${styles.cardContent} ${isSelected ? styles.selected : ''}`}
        onClick={() =>
          onLoadWorld(
            url,
            item.name,
            isWorld ? 'SCENE' : item.modelType || 'SCENE',
            thumbnail
          )
        }
      >
        <img
          src={thumbnail}
          className={styles.cardImage}
          alt={item.name}
          onClick={handleImageClick}
          style={{ cursor: !isWorld && url ? 'pointer' : 'default' }}
        />
        <div className={styles.cardInfo}>
          <h4 className={styles.cardTitle}>{item.name}</h4>
          <Button
            type='primary'
            size='small'
            className={styles.cardButton}
            onClick={(e) => {
              e.stopPropagation();
              onLoadWorld(
                url,
                item.name,
                isWorld ? 'SCENE' : item.modelType || 'SCENE',
                thumbnail
              );
            }}
          >
            {isWorld ? 'Load World' : 'Add to scene'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Cards = ({
  items,
  onLoadWorld,
  activeCategory,
  selectedWorld,
  onOpenPreview,
}) => {
  return (
    <div className={styles.worldsGrid}>
      {items?.map((item, i) => (
        <Card
          key={`${i}-${item.name}`}
          item={item}
          onLoadWorld={onLoadWorld}
          activeCategory={activeCategory}
          selectedWorld={selectedWorld}
          onOpenPreview={onOpenPreview}
        />
      ))}
    </div>
  );
};

export default function WorldsAndPrefabsContent({
  onAddToScene,
  worlds,
  selectedWorld,
}) {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [currentLength, setCurrentLength] = useState<number>(0);
  const [activeCategory, setActiveCategory] = useState<string>(
    CATEGORIES.WORLDS
  );
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [modelUrl, setModelUrl] = useState<string>('');

  const { items, paginatedItems } = useMemo(() => {
    const currentItems =
      activeCategory === CATEGORIES.WORLDS
        ? worlds || []
        : FREE_ASSETS.prefab || [];

    setCurrentLength(currentItems.length);
    return {
      items: currentItems,
      paginatedItems: paginate(currentItems, currentPage),
    };
  }, [activeCategory, worlds, currentPage]);

  const onLoadWorld = useCallback(
    (url, name, modelType, thumbnail) => {
      onAddToScene({
        type: 'WORLD_SCENE',
        name,
        url: url || '',
        baseModelURL: url || '',
        thumbnail: thumbnail || '',
        sceneThumbnail: thumbnail || '',
        modelType: modelType || 'SCENE',
        ref: React.createRef(),
      });
    },
    [onAddToScene]
  );

  const onOpenPreview = useCallback((url) => {
    setPreviewOpen(true);
    setModelUrl(url);
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page - 1);
  };

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.topSection}>
        <h3 className={styles.sectionTitle}>Worlds & Prefabs</h3>
        <Flex align='center' gap={25} style={{ marginBottom: 20 }}>
          <Button
            onClick={() => {
              setActiveCategory(CATEGORIES.WORLDS);
              setCurrentPage(0);
            }}
            className={`${
              activeCategory === CATEGORIES.WORLDS && styles.activeFilter
            } ${styles.filterButton}`}
          >
            Worlds
          </Button>
          <Button
            onClick={() => {
              setActiveCategory(CATEGORIES.PREFABS);
              setCurrentPage(0);
            }}
            className={`${
              activeCategory === CATEGORIES.PREFABS && styles.activeFilter
            } ${styles.filterButton}`}
          >
            Prefabs
          </Button>
        </Flex>
      </div>

      <Cards
        items={paginatedItems}
        onLoadWorld={onLoadWorld}
        activeCategory={activeCategory}
        selectedWorld={selectedWorld}
        onOpenPreview={onOpenPreview}
      />

      <Flex justify='center' style={{ marginTop: 20 }}>
        <Pagination
          current={currentPage + 1}
          pageSize={PAGE_LIMIT}
          total={currentLength}
          hideOnSinglePage
          showSizeChanger={false}
          onChange={handlePageChange}
        />
      </Flex>

      <ModelViewerModal
        open={previewOpen}
        setOpen={setPreviewOpen}
        modelUrl={modelUrl}
      />
    </div>
  );
}
